import { useEffect, useState } from "react";
import { IMaskInput } from "react-imask";
import { createClient } from "../../api/clients";
import { getCompanies } from "../../api/companies";
import "../../styles/pages/client-modal.css";

interface CreateClientModalProps {
  onClose: () => void;
  onCreated: (client: any) => void;   // ← возвращаем созданного клиента
  prefill?: string;                   // ← текст из поиска
}

interface ClientForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  companyName: string;
  tags: string;
}

export default function CreateClientModal({
  onClose,
  onCreated,
  prefill = ""
}: CreateClientModalProps) {
  const [form, setForm] = useState<ClientForm>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    companyName: "",
    tags: "",
  });

  const [companies, setCompanies] = useState<any[]>([]);
  const [emailError, setEmailError] = useState("");

  // Автозаполнение из поиска
  useEffect(() => {
    if (!prefill) return;

    const digits = prefill.replace(/\D/g, "");
    const words = prefill.split(" ");

    if (digits.length >= 5) {
      setForm((f) => ({ ...f, phone: "+7 " + digits }));
    } else {
      setForm((f) => ({
        ...f,
        firstName: words[0] ?? "",
        lastName: words[1] ?? "",
      }));
    }
  }, [prefill]);

  useEffect(() => {
    const load = async () => {
      const res = await getCompanies();
      setCompanies(res);
    };
    load();
  }, []);

  const update = (key: keyof ClientForm, value: any) => {
    setForm({ ...form, [key]: value });
  };

  const validateEmail = (email: string) => {
    if (!email) return true;
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    setEmailError(ok ? "" : "Некорректный email");
    return ok;
  };

  const [dup, setDup] = useState<any | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (force = false) => {
    if (!validateEmail(form.email)) return;
    setError("");
    setSaving(true);
    try {
      const created = await createClient({
        ...form,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim())
          : [],
        ...(force ? { force: true } : {}),
      } as any);

      onCreated(created.data);   // ← возвращаем клиента
      onClose();
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === "DUPLICATE_CLIENT") {
        setDup(data.existing);
      } else {
        const m = data?.message;
        setError(Array.isArray(m) ? m.join(", ") : m || "Не удалось создать клиента");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-window">
        <h2 className="modal-title">Создать клиента</h2>

        <div className="modal-form-grid">
          <input
            className="modal-input"
            placeholder="Имя"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
          />

          <input
            className="modal-input"
            placeholder="Фамилия"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
          />

          <IMaskInput
            mask="+7 (000) 000-00-00"
            className="modal-input"
            placeholder="Телефон"
            value={form.phone}
            onAccept={(value) => update("phone", value)}
          />

          <div>
            <input
              className="modal-input"
              placeholder="Email"
              value={form.email}
              onChange={(e) => {
                update("email", e.target.value);
                validateEmail(e.target.value);
              }}
            />
            {emailError && <div className="input-error">{emailError}</div>}
          </div>

          <select
            className="modal-input"
            value={form.companyName}
            onChange={(e) => update("companyName", e.target.value)}
          >
            <option value="">Компания не выбрана</option>
            {companies.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>

          <input
            className="modal-input"
            placeholder="Теги (через запятую)"
            value={form.tags}
            onChange={(e) => update("tags", e.target.value)}
          />
        </div>

        {dup && (
          <div style={{ margin: "12px 0", padding: "12px 14px", borderRadius: 10, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", color: "#fbbf24", fontSize: 13, lineHeight: 1.5 }}>
            Клиент с таким телефоном уже есть:{" "}
            <b>{[dup.lastName, dup.firstName].filter(Boolean).join(" ")}</b> · {dup.phone}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button className="btn primary" onClick={() => { onCreated(dup); onClose(); }}>
                Использовать этого клиента
              </button>
              <button className="btn" onClick={() => submit(true)} disabled={saving}>
                Всё равно создать нового
              </button>
            </div>
          </div>
        )}
        {error && <div className="input-error" style={{ margin: "8px 0" }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn primary" onClick={() => submit()} disabled={saving}>
            {saving ? "Создание…" : "Создать"}
          </button>
          <button className="btn" onClick={onClose}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
